import React from 'react';
import { Redirect, useParams } from 'react-router-dom';

// Pages
import LoginPage from 'pages/root/LoginPage';
import PropTypes from 'prop-types';

import { usePermissions, useSeller, useUser } from 'hooks';
import { sessionToken } from 'utils';

import BootGate from '../BootProvider/BootGate';

// Components

// Same rules as SellerGuard
function renderSellerGuard({ isAuthenticated, token, isStaff, isSeller, seller, children }) {
  const { error, sellerPermissions } = seller;

  if (!isAuthenticated && !token) return <LoginPage />;

  if (!isStaff && !isSeller) return <Redirect to="/not-authorized" />;

  if (!sellerPermissions && !error) return <BootGate />;

  if (error) return <Redirect to="/not-authorized" />;

  return children;
}

// Same rules as InstitutionGuard
function renderInstitutionGuard({ isAuthenticated, token, isStaff, isInstitution, children }) {
  if (!isAuthenticated && !token) return <LoginPage />;

  if (!isStaff && !isInstitution) return <Redirect to="/not-authorized" />;

  return children;
}

/**
 * Unified guard for the shared panel layout (AppLayout).
 *
 * Replicates the SellerGuard/InstitutionGuard rules (and the former
 * AccountGuard's) branched on the matched route params. It must be a single
 * component (stable identity) so the
 * chrome below it survives context switches — rendering the original guard
 * components conditionally would change the element type and remount the tree.
 */
function PanelGuard({ children }) {
  // Custom hooks
  const { isAuthenticated } = useUser();
  const { permissions } = usePermissions();
  const { sellerSlug, institutionSlug } = useParams();
  const { error, sellerPermissions } = useSeller();

  // Constants
  const { isSeller, isInstitution, isStaff } = permissions || {};
  const { token } = sessionToken();

  if (sellerSlug)
    return renderSellerGuard({
      isAuthenticated,
      token,
      isStaff,
      isSeller,
      seller: { error, sellerPermissions },
      children,
    });

  if (institutionSlug)
    return renderInstitutionGuard({ isAuthenticated, token, isStaff, isInstitution, children });

  // Accounts context: same rules as the former AccountGuard
  if (!isAuthenticated) return <LoginPage />;

  return children;
}

PanelGuard.propTypes = {
  children: PropTypes.any,
};

export default PanelGuard;
